import React, { useCallback, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useMutation } from "@apollo/client";

// region Libraries
import Yup from "@libraries/yup";
import { TextField } from "@libraries/mui";
import { Button, useToast } from "@npm-telluria-tecnologia/telluria-ui";
// endregion Libraries
// region Store
import {
  TUserEmailMutations,
  userForgotPassword
} from "@graphql/groups/user/mutation.graphql";
import { UserMutationsUserEmailArgs } from "@graphql/helpers/types.graphql";
import utils from "@helpers/utils";
// endregion Store
// region Language
import useTranslation from "@languages/useTranslation";
import { ForgotPasswordMessages } from "@languages/interfaces/forgotPasswordMessages";
// endregion Language
// region Atoms
import ExternalSvg from "@atoms/ExternalSvg";
// endregion Atoms
// region Molcules
import PageHeader from "@molecules/PageHeader";
// endregion Molcules
// region Styles
import * as Styled from "./styles";

// endregion Styles

interface IForgotPasswordFormData {
  email: string;
}

const ForgotPasswordForm: React.FC = () => {

  const { t } = useTranslation();
  const { addToast } = useToast();
  const [isLoading, setIsLoading] = useState(false);

  const [MutationForgotPasswordUser] = useMutation<TUserEmailMutations,
    UserMutationsUserEmailArgs>(
      userForgotPassword()
    );

  // Define the validation types
  const forgotPasswordSchema = Yup.object()
    .shape({
      email: Yup.string()
        .trim()
        .required(t(ForgotPasswordMessages.forgotPasswordRequiredEmail))
        .email(t(ForgotPasswordMessages.forgotPasswordInvalidEmail)),
      fullName: Yup.string()
    });

  // Form control
  const {
    control,
    handleSubmit,
    formState: { errors: formErrors }
  } = useForm<IForgotPasswordFormData>({
    resolver: yupResolver(forgotPasswordSchema),
    reValidateMode: "onChange",
    mode: "onBlur"
  });

  /**
   * Send mail to reset password
   * @param data User data to send
   */
  const sendMailResetPassword = useCallback(async (data: IForgotPasswordFormData) => {

    const { email } = data;
    const mutation = await MutationForgotPasswordUser({
      variables: {
        user: {
          email
        }
      }
    });

    return mutation.data?.user.userEmail;
  }, []);

  /**
   * Handle submit form
   * @param data User data from form
   */
  const forgotPassword = useCallback(async (data) => {

    setIsLoading(true);

    try {

      const response = await sendMailResetPassword(data);
      const toastStatus = utils.getToastStatusType(response?.status);

      if (response?.status === "SUCCESS") {
        addToast({
          type: toastStatus,
          title: response!.message
        });
      }

    } catch (error) {
      addToast({
        type: "error",
        title: "Internal client error",
        description: error.message
      });
    } finally {
      setIsLoading(false);
    }

  }, []);

  // Handle form controllers
  const handleSubmitForm: SubmitHandler<IForgotPasswordFormData> = (formData: IForgotPasswordFormData) => forgotPassword(formData);

  return (
    <Styled.ForgotPasswordFormContainer>
      <Styled.ForgotPasswordFormLogo>
        <ExternalSvg svgName="LogotipoMinifiedColor" />
      </Styled.ForgotPasswordFormLogo>
      <PageHeader url="/login" title={t(ForgotPasswordMessages.forgotPasswordTitlePage)} />
      <form onSubmit={handleSubmit(handleSubmitForm)}>
        <Controller // Email
          name="email"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              autoComplete="username"
              label={t(ForgotPasswordMessages.forgotPasswordEmailLabel)}
              type="email"
              variant="outlined"
              fullWidth
              size="small"
              error={!!formErrors.email}
              helperText={formErrors.email && formErrors.email.message}
              className="default-field"
            />
          )}
        />
        <Button
          variant="contained"
          size="small"
          type="submit"
          text={t(ForgotPasswordMessages.forgotPasswordButton)}
          loading={isLoading}
        />
      </form>
    </Styled.ForgotPasswordFormContainer>
  );
};

export default ForgotPasswordForm;
